import { browser } from '$app/env';
import { getFromLocalStorage, writeToLocalStorage } from '#src/util/local-storage-sync.svelte.ts';

interface ResizableOptions {
	side: 'left' | 'right';
	minWidth: number;
	maxWidth: number;
	storageKey?: string;
}

export function resizable(options: ResizableOptions) {
	return (element: HTMLElement) => {
		if (!browser) {
			return;
		}

		const { side, minWidth, maxWidth, storageKey } = options;

		// Load initial width from storage if available
		if (storageKey) {
			const savedWidth = getFromLocalStorage(storageKey);
			if (savedWidth !== null) {
				element.style.width = `${savedWidth}px`;
			}
		}

		// Create resize handle
		const handle = document.createElement('div');
		handle.style.position = 'absolute';
		handle.style.top = '0';
		handle.style.bottom = '0';
		handle.style.width = '4px';
		handle.style.cursor = 'col-resize';
		handle.style.zIndex = '10';
		handle.style.transition = 'background-color 0.2s';

		if (side === 'left') {
			handle.style.left = '0px';
		} else {
			handle.style.right = '0px';
		}

		// Handle styling on hover
		const handleMouseEnter = () => {
			handle.style.backgroundColor = 'var(--surface-3)';
		};

		const handleMouseLeave = () => {
			handle.style.backgroundColor = 'transparent';
		};

		handle.addEventListener('mouseenter', handleMouseEnter);
		handle.addEventListener('mouseleave', handleMouseLeave);

		// Ensure element has position relative for absolute handle
		if (getComputedStyle(element).position === 'static') {
			element.style.position = 'relative';
		}

		element.appendChild(handle);

		// Drag state
		let isDragging = false;
		let startX = 0;
		let startWidth = 0;

		const handleMouseDown = (e: MouseEvent) => {
			isDragging = true;
			startX = e.clientX;
			startWidth = element.offsetWidth;

			// Prevent text selection during drag
			e.preventDefault();
			document.body.style.userSelect = 'none';
			document.body.style.cursor = 'col-resize';
		};

		const handleMouseMove = (e: MouseEvent) => {
			if (!isDragging) {
				return;
			}
			const delta = side === 'right' ? e.clientX - startX : startX - e.clientX;
			const newWidth = Math.max(minWidth, Math.min(maxWidth, startWidth + delta));
			element.style.width = `${newWidth}px`;
		};

		const handleMouseUp = () => {
			if (!isDragging) {
				return;
			}

			isDragging = false;
			document.body.style.userSelect = '';
			document.body.style.cursor = '';

			// Save to localStorage
			if (storageKey) {
				const currentWidth = element.offsetWidth;
				writeToLocalStorage(storageKey, currentWidth);
			}
		};

		handle.addEventListener('mousedown', handleMouseDown);
		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);

		// Cleanup
		return () => {
			handle.removeEventListener('mouseenter', handleMouseEnter);
			handle.removeEventListener('mouseleave', handleMouseLeave);
			handle.removeEventListener('mousedown', handleMouseDown);
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
			element.removeChild(handle);
		};
	};
}
